import { Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import InteractiveWebRuleInline from "components/selection-components/select-activity/interactive-web-rule-inline/interactive-web-rule-inline";
import SimpleWebRule from "components/selection-components/select-activity/simple-web-rule-title/simple-web-rule";
import { Time } from "store/store-types";
import { WebRuleType } from "types/web-rule-type";
import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";

interface RenderInteractiveComponentsProps {
    webRule: WebRule;
    index: number;
    selectedTime: TimeSlot | undefined;
    onTimeClick: (timeSlot: TimeSlot) => void;
    selectedWaitListTimes: TimeSlot[];
    onWaitListTimeClick: (timeSlot: TimeSlot) => void;
    timesToNudgeMap: Map<number, Time[]>;
    showStartTimeHeading?: boolean;
    hideWebRuleHeader?: boolean;
    ignoredRuleIds?: Set<number>;
    chainConflictStartTimes?: Set<string>;
}

/** Renders components where the user can choose time slots */
const RenderInteractiveComponents = ({
    webRule,
    index,
    selectedTime,
    onTimeClick,
    selectedWaitListTimes,
    onWaitListTimeClick,
    timesToNudgeMap,
    showStartTimeHeading,
    hideWebRuleHeader,
    ignoredRuleIds,
    chainConflictStartTimes,
}: RenderInteractiveComponentsProps) => {
    return (
        <InteractiveWebRuleInline
            key={`interactive-${webRule.id}-${index}`}
            webRule={webRule}
            selectedTime={selectedTime ?? null}
            onTimeClick={onTimeClick}
            selectedWaitListTimes={selectedWaitListTimes}
            onWaitListTimeClick={onWaitListTimeClick}
            timesToNudge={timesToNudgeMap.get(webRule.id) ?? []}
            showStartTimeHeading={showStartTimeHeading}
            hideWebRuleHeader={hideWebRuleHeader}
            ignoredRuleIds={ignoredRuleIds}
            chainConflictStartTimes={chainConflictStartTimes}
        />
    );
};

interface WebRuleRendererProps {
    orderedSequence: Array<{
        type: WebRuleType;
        webRule: WebRule;
    }>;
    selectedTime: TimeSlot | undefined;
    onTimeClick: (timeSlot: TimeSlot) => void;
    selectedWaitListTimes: TimeSlot[];
    onWaitListTimeClick: (timeSlot: TimeSlot) => void;
    timesToNudgeMap: Map<number, Time[]>;
    showStartTimeHeading?: boolean;
    hideWebRuleHeader?: boolean;
    ignoredRuleIds?: Set<number>;
    chainConflictStartTimes?: Set<string>;
}

/**
 * Renders the web rules with correct layout (title, description, accordion, timeslot grid)
 */
const WebRuleRenderer = ({
    orderedSequence,
    selectedTime,
    onTimeClick,
    selectedWaitListTimes,
    onWaitListTimeClick,
    timesToNudgeMap,
    showStartTimeHeading,
    hideWebRuleHeader,
    ignoredRuleIds,
    chainConflictStartTimes,
}: WebRuleRendererProps) => {
    return (
        <Stack spacing={ThemeSpaceVariable.Medium}>
            {orderedSequence.map((item, index) => {
                if (item.type === WebRuleType.Interactive) {
                    return (
                        <RenderInteractiveComponents
                            key={`interactive-${item.webRule.id}-${index}`}
                            webRule={item.webRule}
                            index={index}
                            selectedTime={selectedTime}
                            onTimeClick={onTimeClick}
                            selectedWaitListTimes={selectedWaitListTimes}
                            onWaitListTimeClick={onWaitListTimeClick}
                            timesToNudgeMap={timesToNudgeMap}
                            showStartTimeHeading={showStartTimeHeading}
                            hideWebRuleHeader={hideWebRuleHeader}
                            ignoredRuleIds={ignoredRuleIds}
                            chainConflictStartTimes={chainConflictStartTimes}
                        />
                    );
                } else {
                    return (
                        <SimpleWebRule
                            key={`comment-only-${item.webRule.id}-${index}`}
                            webRule={item.webRule}
                        />
                    );
                }
            })}
        </Stack>
    );
};

export default WebRuleRenderer;
